import { resolveIcon } from '../../styles/icons'
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import DropDown from '../generic/DropDown.jsx'
import useScreenSize from '../../hooks/useScreenSize.jsx'
import useApi from '../../hooks/useApi.jsx'
import useTranslation from '../../hooks/useTranslation.jsx'
import GroupFilterMenuItem from './GroupFilterMenuItem.jsx'
import useOutsideClick from '../../hooks/useOutsideClick.jsx'

const Container = styled.div`
  display: inline-flex;
  align-items: center;
`

const ToggleInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
`

const SearchWrap = styled.div`
  padding: 0.25rem 0.5rem 0.25rem 0.5rem;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.dropdown.background};
  z-index: 1;
`

const SearchInput = styled.input`
  width: 100%;
  height: 2rem;
  font-size: var(--font-XS);
  color: ${({ theme }) => theme.font.color};
  background: ${({ theme }) => theme.page?.background || 'transparent'};
  border: 1px solid ${({ theme }) => theme.area.border};
  border-radius: 0.375rem;
  padding: 0 1.5rem 0 0.5rem; /* reserve space for clear button */
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.primary?.active || theme.font.active};
  }
`

const ClearBtn = styled.button`
  position: absolute;
  top: 0.375rem;
  right: 0.75rem;
  width: 1.25rem;
  height: 1.25rem;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font.color};
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.75;
  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.secondary?.active || theme.font.active};
  }
`

/**
 * Normalize an item's sort key.
 * - Prefer configured sortField
 * - Fallback to id
 */
const getSortKey = (item, sortField) => {
  const v = (item?.[sortField] ?? item?.id ?? '').toString()
  return v
}

/**
 * Compute depth for hierarchical display.
 * - If hierarchyField is 'type' and values like 'normal' / 'subGroup', map to 0/1
 * - Otherwise fallback to number of dots in sortKey (e.g., "Parent.Child" -> 1)
 */
const computeDepth = (item, hierarchyField, sortKey, parentIdField) => {
  if (hierarchyField) {
    const val = item?.[hierarchyField]
    if (hierarchyField === 'type') {
      if (val === 'normal') return 0
      if (val === 'subGroup') return 1
      // If type is missing or unexpected, fall back to parent relationship
      if (parentIdField && item?.[parentIdField]) return 1
      return 0
    }
  }
  if (sortKey && typeof sortKey === 'string') {
    const dots = (sortKey.match(/\./g) || []).length
    return dots
  }
  // Final fallback: if parentId exists, indent as child
  if (parentIdField && item?.[parentIdField]) return 1
  return 0
}

/**
 * sortList
 * Returns a flattened array [{ item, depth }] for hierarchical rendering.
 * - If every item has a non-empty sortField, sort by that and compute depth via computeDepth.
 * - Otherwise:
 *   1) Sort parents (type==='normal' OR !parentId) by id (string)
 *   2) For each parent, append its children (type==='subGroup' OR parentId===parent.id) sorted by id
 *   3) Append any orphans (children whose parent missing) sorted by id after parents
 */
const sortList = (items, sortField, hierarchyField, parentIdField, displayField) => {
  // Prefer virtual sort keys when present
  const allHaveVSort = Array.isArray(items) && items.length > 0
    ? items.every(it => typeof it?._vSortKey === 'string' && it._vSortKey.length > 0)
    : false

  if (allHaveVSort) {
    const mapped = items.map(it => {
      const sk = it._vSortKey
      const depth = computeDepth(it, hierarchyField, sk, parentIdField)
      return { item: it, depth, sortKey: sk }
    })
    mapped.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    return mapped.map(({ item, depth }) => ({ item, depth }))
  }

  // Fallback grouping using type/parentId with display-based sorting
  const isParent = (it) => {
    if (hierarchyField === 'type') {
      if (it?.type === 'subGroup') return false
      return true
    }
    return !it?.parentId
  }

  const byDisplayAsc = (a, b) => (String(a?.[displayField] ?? a?.id ?? '')).localeCompare(String(b?.[displayField] ?? b?.id ?? ''))

  const parents = items.filter(isParent).sort(byDisplayAsc)
  const children = items.filter(it => !isParent(it))
  const childrenByParent = new Map()
  for (const ch of children) {
    const pid = ch?.parentId ?? ''
    if (!childrenByParent.has(pid)) childrenByParent.set(pid, [])
    childrenByParent.get(pid).push(ch)
  }
  for (const [pid, arr] of childrenByParent.entries()) {
    arr.sort(byDisplayAsc)
  }

  const out = []
  const appended = new Set()
  for (const p of parents) {
    out.push({ item: p, depth: 0 })
    appended.add(p.id)
    const arr = childrenByParent.get(p.id) || []
    for (const ch of arr) {
      out.push({ item: ch, depth: 1 })
      appended.add(ch.id)
    }
  }

  // Orphans or items not covered by parent/child detection
  const leftovers = items.filter(it => !appended.has(it.id)).sort(byDisplayAsc)
  for (const it of leftovers) {
    const depth = it?.parentId ? 1 : 0
    out.push({ item: it, depth })
  }

  return out
}

/**
 * GroupFilterSelector
 * Props:
 * - config: {
 *     label: string (i18n key),
 *     endpoint: string,        // filter key and API endpoint to fetch options
 *     menuDisplay: 'flat' | 'hierarchial',
 *     sortField: string,       // e.g. 'sortKey'
 *     hierarchyField: string,  // e.g. 'type'
 *     icon: ReactNode,         // from styles/icons
 *     multiSelect: boolean
 *   }
 * - value: string[] (selected ids for this selector)
 * - onChange(nextSelectedIds: string[], ctx: { toggledId: string, added: boolean }): void
 */
const GroupFilterSelector = ({ config, value = [], onChange }) => {
  const { isMobile } = useScreenSize()
  const { t } = useTranslation()
  const { getMe } = useApi()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Close dropdown when clicking outside the selector
  const rootRef = useRef(null)
  useOutsideClick(rootRef, () => setOpen(false), 'normal')

  const label = config?.label ? (t ? t(config.label) : config.label) : ''
  const Icon = resolveIcon(config?.icon)

  const sortField = config?.sortField || 'id'
  const hierarchyField = config?.hierarchyField || null
  const menuDisplay = (config?.menuDisplay || '').toLowerCase().startsWith('hierarch') ? 'hierarchial' : 'flat'
  const endpoint = config?.endpoint
  const subEndpoint = config?.subEndpoint || null
  const parentIdField = config?.parentIdField || 'parentId'
  const displayField = config?.displayField || 'id'
  const descriptionField = config?.descriptionField || 'description'
  const filterField = config?.filterField || 'id'
  const subFilterField = config?.subFilterField || 'id'

  useEffect(() => {
    //console.log('[i] items -> ', items)
 
  }, [items])

  const fetchData = useCallback(async () => {
    if (!endpoint) return
    setLoading(true)
    try {
      // Ask server to order by sortField when possible
      const resource = `${endpoint}/?orderBy=${encodeURIComponent(sortField)}`
      const res = await getMe(resource)
      // Use data as-is so existing 'type' (e.g., 'normal'/'subGroup') and parentId are preserved
      const parents = (res?.data || [])
      let allItems = parents

      if (subEndpoint) {
        // Fetch children for each parent (parallel)
        const childrenArrays = await Promise.all(
          parents.map(async (p) => {
            const subRes = await getMe(`${endpoint}/${encodeURIComponent(p.id)}/${subEndpoint}`)
            const subData = subRes?.data || []
            return subData.map(ch => ({
              ...ch,
              parentId: ch[parentIdField],
              type: 'subGroup',
              _filterField: subFilterField
            }))
          })
        )
        const children = childrenArrays.flat()
        allItems = [...parents, ...children]
      }

      // Create virtual sortKey to ensure parent-then-children grouping sorted by display values
      const parentDisplayMap = new Map(parents.map(p => [p.id, String(p[displayField] ?? p.id)]))
      allItems = allItems.map(it => {
        const isChild = !!it[parentIdField]
        const parentDisp = isChild ? (parentDisplayMap.get(it[parentIdField]) || '') : ''
        const selfDisp = String(it[displayField] ?? it.id)
        const vSort = isChild && parentDisp ? `${parentDisp}.${selfDisp}` : (selfDisp || String(it.id))
        if (!it._filterField) it._filterField = filterField
        return { ...it, _vSortKey: vSort  }
      })

      // Local sort as a safeguard (use virtual sortKey if present)
      allItems.sort((a, b) => String(a._vSortKey || getSortKey(a, sortField)).localeCompare(String(b._vSortKey || getSortKey(b, sortField))))
      setItems(allItems)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, sortField, displayField, parentIdField])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const flatOrHierList = useMemo(() => {
    if (!Array.isArray(items)) return []
    if (menuDisplay === 'flat') {
      return items.map(it => ({ item: it, depth: 0, sortKey: getSortKey(it, sortField) }))
    }
    // hierarchial
    const mapped = sortList(items, sortField, hierarchyField, parentIdField, displayField)
    return mapped
  }, [items, menuDisplay, sortField, hierarchyField])

  const displayList = useMemo(() => {
    const q = (searchTerm || '').toLowerCase().trim()
    if (!q) return flatOrHierList
    // Flatten and filter by id/display/description (case-insensitive)
    const flattened = (items || []).map(it => ({ item: it, depth: 0 }))
    const filtered = flattened.filter(({ item }) => {
      const idStr = (item?.id ?? '').toString().toLowerCase()
      const primary = (item?.[displayField] ?? '').toString().toLowerCase()
      const secondary = (item?.[descriptionField] ?? '').toString().toLowerCase()
      return idStr.includes(q) || primary.includes(q) || secondary.includes(q)
    })
    // Keep deterministic order
    filtered.sort((a, b) => getSortKey(a.item, sortField).localeCompare(getSortKey(b.item, sortField)))
    return filtered
  }, [searchTerm, flatOrHierList, items, sortField, displayField, descriptionField])

  // Normalize selected values into pairs [value, field] so we can mix parent/child filters
  const currentPairs = useMemo(() => {
    if (!Array.isArray(value)) return []
    return value.map(v => {
      if (Array.isArray(v)) {
        // [val, field]
        return [String(v[0]), String(v[1] ?? filterField)]
      }
      // primitive -> assume main filterField
      return [String(v), String(filterField)]
    })
  }, [value, filterField])

  const tokenOf = (val, field) => `${field}|${val}`

  const selectedTokens = useMemo(() => {
    const s = new Set()
    currentPairs.forEach(([v, f]) => s.add(tokenOf(v, f)))
    return s
  }, [currentPairs])

  const handleToggle = (id, item) => {
    if (!id) return
    const filterToUse = item?._filterField || filterField
    const valueKey = (item?.[filterToUse] ?? id)?.toString()
    const primary = (item?.[displayField] ?? item?.id ?? '').toString()
    const isChild = (filterToUse === subFilterField) || item?.type === 'subGroup' || !!item?.[parentIdField]
    const rawPrefix = (isChild && config?.subLabel) ? config.subLabel : config?.label
    const labelPrefix = rawPrefix ? (t ? t(rawPrefix) : rawPrefix) : ''
    const displayLabel = labelPrefix ? `${labelPrefix}: ${primary}` : primary

    // Toggle pair [valueKey, filterToUse]
    const tok = `${filterToUse}|${valueKey}`
    let nextPairs = [...currentPairs]
    const idx = nextPairs.findIndex(([v, f]) => v === valueKey && f === filterToUse)
    let added = false
    if (idx >= 0) {
      nextPairs.splice(idx, 1)
    } else {
      if (!config?.multiSelect) nextPairs = []
      nextPairs.push([valueKey, filterToUse])
      added = true
    }

    onChange?.(nextPairs, { toggledId: valueKey, toggledField: filterToUse, added, item, displayLabel })
  }

  return (
    <Container ref={rootRef}>
      <DropDown $margin="0">
        <DropDown.Toggle $bg={open ? 'active' : undefined} $hasBorder='true' onClick={() => setOpen(o => !o)}>
          <ToggleInner>
            {Icon ? <IconWrap>{Icon}</IconWrap> : null}
            {!isMobile && label}
          </ToggleInner>
        </DropDown.Toggle>
        {open && (
          <DropDown.Menu role="menu" aria-label={label} $leftAligned={'true'}>
            {loading && <DropDown.Item $inactive>Loading...</DropDown.Item>}
            {!loading && (
              <>
                <SearchWrap>
                  <SearchInput
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t ? (t('all.list.search') || 'Search') : 'Search'}
                    aria-label="Search"
                  />
                  {searchTerm && (
                    <ClearBtn onClick={() => setSearchTerm('')} aria-label="Clear search">×</ClearBtn>
                  )}
                </SearchWrap>
                {displayList.map(({ item, depth }) => {
                  const id = item?.id
                  const filterToUse = item?._filterField || filterField
                  const valueKey = (item?.[filterToUse] ?? id)?.toString()
                  const selected = selectedTokens.has(tokenOf(valueKey, filterToUse))
                  return (
                    <GroupFilterMenuItem
                      key={id}
                      item={item}
                      depth={depth}
                      selected={selected}
                      onToggle={handleToggle}
                      displayField={displayField}
                      descriptionField={descriptionField}
                    />
                  )
                })}
                {displayList.length === 0 && <DropDown.Item $inactive>No results</DropDown.Item>}
              </>
            )}
          </DropDown.Menu>
        )}
      </DropDown>
    </Container>
  )
}

export default GroupFilterSelector
